/**
 * ContactManagerModal — focus-trapped modal for the "request access" flow.
 *
 * `onSubmit({ name, email, message, subdomain, website })` is REQUIRED and is the only way the
 * modal reports success: it resolves → the confirmation view; it rejects → a translated alert
 * over the still-filled form, so the employee can retry. `subdomain` is what the REQUIRED
 * `resolveSubdomain()` answers when the request is sent.
 *
 * 🔴 UNREACHABLE-MODULE-BURNDOWN-001 L16: this modal used to SIMULATE success. With no `onSubmit`
 * it waited 500 ms and showed the confirmation, and its `catch` showed the confirmation too — and
 * no tenant `<LoginShell>` passed one, so every tenant sign-in page told an employee "Your request
 * has been sent to your store manager" while sending nothing. The router already answers
 * enumeration-safely on its own (a honeypot hit and an unknown subdomain both get the generic
 * success), so every failure that reaches this modal is REAL — a validation refusal, a throttle, a
 * persistence error, a dropped connection — and hiding it was never protecting anything.
 *
 * `website` is the canonical `<HoneypotField>`'s value, named for the field
 * `createAccessRequestRouter` checks. palm-web's retired `AccessRequestForm` rendered a decoy with
 * that name too — uncontrolled and never sent, so its bot check could not fire.
 *
 * @module @puffin-core/login-shell/components/ContactManagerModal
 */

import { useRef, useCallback, useReducer } from 'react';
import PropTypes from 'prop-types';
// 🔴 The NARROW subpath, never the barrel. `@puffin-core/page-shell`'s index pulls
// `react-router-dom` (through `useManageAccountAction`), and login-shell is deliberately
// router-free — importing the barrel here dragged the router in and took 21 of this
// package's suites down with `ReferenceError: TextEncoder is not defined`, including ones
// that touch no modal at all. `./modal-chrome` joins the existing narrow exports
// (`./section-pill`, `./honeypot-field`, …) that exist for exactly this reason.
import { Modal } from '@puffin-core/page-shell/modal';
import { HoneypotField } from '@puffin-core/page-shell/honeypot-field';
import { InlineFeedback } from '@puffin-core/page-shell/inline-feedback';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';

const INITIAL_STATE = {
  success: false,
  name: '',
  email: '',
  message: '',
  isLoading: false,
  errorKey: null,
};

/**
 * The router field `createAccessRequestRouter` reads its bot check from. Server-side detection is
 * name-SENSITIVE (see `<HoneypotField>`'s docblock), so this is a contract, not a label.
 */
const HONEYPOT_FIELD_NAME = 'website';

/**
 * Which sentence a refusal earns. The status is the only thing read: the server's own message is
 * English, and the router already answers enumeration-safely, so the class of failure is all the
 * employee needs — and all three have a different next step.
 *
 * @param {unknown} err - the rejection from `onSubmit`
 * @returns {string} a `modal.contact.error.*` key
 */
function errorKeyFor(err) {
  const status = err?.status;
  if (status === 429) return 'modal.contact.error.rateLimited';
  if (status === 400 || status === 422) return 'modal.contact.error.invalid';
  return 'modal.contact.error.failed';
}

// Exported for branch-coverage testing of the defensive `default:` case
// of the reducer's switch. Dispatch call sites in this file always use
// one of the listed cases, so the default arm cannot be reached through
// the public component API; consumers should not import this directly.
export function __testContactReducer(state, action) {
  return reducer(state, action);
}

function reducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value, errorKey: null };
    case 'RESET':
      return INITIAL_STATE;
    case 'SUBMIT_START':
      return { ...state, isLoading: true, errorKey: null };
    case 'SUBMIT_COMPLETE':
      return { ...state, isLoading: false, success: true };
    case 'SUBMIT_FAILED':
      return { ...state, isLoading: false, errorKey: action.errorKey };
    default:
      return state;
  }
}

/**
 * The access-request form body. Extracted to module scope so the modal's own
 * markup stays a shallow success-vs-form branch — inline, this form pushed the
 * modal's JSX six levels deep. Module-private (never exported from index.js).
 */
function ContactRequestForm({
  state,
  translate,
  companyNameDisplay,
  handleSubmit,
  handleField,
  honeypotRef,
}) {
  const isIncomplete = state.name.trim() === '' || state.email.trim() === '';
  return (
    <>
      <p className="form-description">
        {translate('modal.contact.body', { companyName: companyNameDisplay })}
      </p>
      <form onSubmit={handleSubmit} aria-label={translate('modal.contact.formLabel')}>
        {/* SAVE-FEEDBACK-001 (SAVE-FEEDBACK-CANON-001 P3): the refused request is the ONE feedback
            component — `role="alert"` is its, never hand-written `.alert` markup. The form spaces
            nothing itself, so the margin that kept the alert off the first field stays, on a wrapper. */}
        {state.errorKey ? (
          <div className="mb-md">
            <InlineFeedback
              tone="error"
              message={translate(state.errorKey)}
              testId="contact-error"
            />
          </div>
        ) : null}
        <div className="form-group">
          <label className="form-label" htmlFor="request-name">
            {translate('modal.contact.name')}
          </label>
          <input
            className="form-input"
            id="request-name"
            type="text"
            value={state.name}
            onChange={handleField('name')}
            aria-required="true"
            autoComplete="name"
            disabled={state.isLoading}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="request-email">
            {translate('modal.contact.email')}
          </label>
          <input
            className="form-input"
            id="request-email"
            type="email"
            value={state.email}
            onChange={handleField('email')}
            aria-required="true"
            autoComplete="email"
            disabled={state.isLoading}
          />
        </div>
        <div className="form-group">
          <label className="form-label" htmlFor="request-message">
            {translate('modal.contact.message')}
          </label>
          <textarea
            className="form-input"
            id="request-message"
            value={state.message}
            onChange={handleField('message')}
            rows={3}
            disabled={state.isLoading}
          />
        </div>
        <HoneypotField ref={honeypotRef} name={HONEYPOT_FIELD_NAME} />
        <button
          type="submit"
          className="btn btn-primary btn-lg w-full"
          disabled={isIncomplete || state.isLoading}
          aria-busy={state.isLoading ? 'true' : 'false'}
        >
          {state.isLoading
            ? translate('modal.contact.submit.loading')
            : translate('modal.contact.submit')}
        </button>
      </form>
    </>
  );
}

ContactRequestForm.propTypes = {
  state: PropTypes.object.isRequired,
  translate: PropTypes.func.isRequired,
  companyNameDisplay: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleField: PropTypes.func.isRequired,
  honeypotRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
};

export function ContactManagerModal({
  isOpen,
  onClose,
  companyName = '',
  resolveSubdomain,
  onSubmit,
  t,
}) {
  const hasBeenOpenedRef = useRef(false);
  const honeypotRef = useRef(null);
  // No `isMounted` ceremony — React 19 silently no-ops setState on an
  // unmounted component (see GUIDES/shared/standards/testing/react-19-unmount-semantics.md).
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  // Reset on RE-open only — the very first open keeps the pristine initial
  // state (hasBeenOpenedRef gates it), which is the behavior this modal has
  // always had. Stable identity: the chrome effect must not re-run per render.
  const handleOpen = useCallback(() => {
    if (hasBeenOpenedRef.current) {
      dispatch({ type: 'RESET' });
    }
    hasBeenOpenedRef.current = true;
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      // The submit button is disabled for a blank field or an in-flight request, but Enter in a
      // field submits the form without consulting the button — so the guard lives here too.
      if (state.name.trim() === '' || state.email.trim() === '' || state.isLoading) return;
      dispatch({ type: 'SUBMIT_START' });
      try {
        // PLANNER-PALM-UAT-2026-11-03-001 U1: the company the sign-in resolves, asked when the request is SENT. The
        // form's typed subdomain alone was `''` on every tenant page, and the router refused the request for it.
        const subdomain = await resolveSubdomain();
        await onSubmit({
          name: state.name.trim(),
          email: state.email.trim(),
          message: state.message.trim(),
          subdomain,
          website: honeypotRef.current.value,
        });
        dispatch({ type: 'SUBMIT_COMPLETE' });
      } catch (err) {
        dispatch({ type: 'SUBMIT_FAILED', errorKey: errorKeyFor(err) });
      }
    },
    [state.name, state.email, state.message, state.isLoading, onSubmit, resolveSubdomain],
  );

  const handleField = useCallback(
    (field) => (e) => dispatch({ type: 'SET_FIELD', field, value: e.target.value }),
    [],
  );

  // MODALCHROME-001 (owner ruling R-9): the ONE chrome, from page-shell. login-shell's local
  // `useModalPresentation` + `useFocusTrap` were one of THREE implementations of this concern and
  // are deleted. Two behaviours improve in the swap, and both were defects here:
  //   - the scroll lock is the canonical `body.modal-open` CLASS, not `body.style.overflow`
  //     (inline style is forbidden ecosystem-wide, and `modals.css` already pairs with the class);
  //   - Escape is a DOCUMENT-level listener, so it works immediately. The `onKeyDown` it replaces
  //     was on the overlay, so it only fired once focus was already inside.
  // Focus restore is now guarded by `document.contains`, so it cannot steal focus back to a
  // detached node.
  // AAS-I18N-002 W2: the consumer→`login` namespace→English chain plus the
  // `{placeholder}` interpolation this modal used to do inline now live in
  // `useLoginTranslate`, so every login-shell surface interpolates identically.
  const translate = useLoginTranslate(t);

  const companyNameDisplay = companyName || translate('modal.contact.fallbackCompany');

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpen={handleOpen}
      // The success view REPLACES the form, so the dialog's focusable set changes underneath the
      // Tab cycle. Without this dep the trap keeps the form's first/last and a Tab walks out.
      title={translate('modal.contact.title')}
      closeLabel={translate('modal.closeLabel')}
      testId="contact-manager-modal"
    >
      {state.success ? (
        <div data-testid="contact-success">
          <p className="text text-sm text-center">{translate('modal.contact.success')}</p>
          <button type="button" className="btn btn-primary btn-lg w-full" onClick={onClose}>
            {translate('modal.contact.close')}
          </button>
        </div>
      ) : (
        <ContactRequestForm
          state={state}
          translate={translate}
          companyNameDisplay={companyNameDisplay}
          handleSubmit={handleSubmit}
          handleField={handleField}
          honeypotRef={honeypotRef}
        />
      )}
    </Modal>
  );
}

ContactManagerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  companyName: PropTypes.string,
  // `() => string | Promise<string>` — the company the request is for (`<LoginForm>`'s `resolveTenant`).
  resolveSubdomain: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  t: PropTypes.func,
};

export default ContactManagerModal;
